import { captureException } from '@sentry/react'
import axiosOriginalInstance, { type AxiosProgressEvent } from 'axios'
import { z } from 'zod'
import axios from '@/axios-instance'
import axiosNode from '@/axios-instance-node'
import type { VettingItemStatus } from '@/features/ops-vetting/types.ts'
import {
	type EditFlagsItemUrlParam,
	type EditFlagsSectionUrlParam,
	type ServiceFlag,
	ServiceGalleryImage,
	ServiceGalleryImageProPhotos,
	ServiceProPhotos
} from '@/features/service-vetting/types.ts'
import { parseResponseData } from '@/helpers/utils'
import type { ResponseType } from '@/types.ts'

/**
 * httpGetAdminService
 * Get informations from a single submission
 */
interface HttpGetService {
	urlParams: { serviceId: number }
	params: { response_type: ResponseType }
}

export const httpGetService = ({ urlParams, params }: HttpGetService) =>
	axios.get(`/luminate/services/${urlParams.serviceId}`, {
		params
	})

interface HttpGetServiceProPhotosOptions {
	urlParams: { serviceId: number }
	params: { responseType: ResponseType }
}

export const httpGetServiceProPhotos = async ({
	urlParams,
	params
}: HttpGetServiceProPhotosOptions) => {
	const response = await axiosNode.get(
		`v1/services/vetting/submissions/${urlParams.serviceId}`,
		{
			params
		}
	)
	return parseResponseData({
		responseData: response.data,
		responseShape: ServiceProPhotos,
		onZodError: captureException
	})
}
export interface HttpUpdateExpertiseStatus {
	urlParams: { serviceId: number; expertiseId: number }
	payload: { status: VettingItemStatus }
	params: { response_type: ResponseType }
}

export const httpUpdateExpertiseStatus = ({
	urlParams,
	payload,
	params
}: HttpUpdateExpertiseStatus) =>
	axios.patch(
		`/luminate/services/${urlParams.serviceId}/expertise/${urlParams.expertiseId}/update-status`,
		payload,
		{
			params
		}
	)

export interface HttpUpdateOfferingStatus {
	urlParams: { serviceId: number; offeringId: number }
	payload: { status: VettingItemStatus }
	params: { response_type: ResponseType }
}

export const httpUpdateOfferingStatus = ({
	urlParams,
	payload,
	params
}: HttpUpdateOfferingStatus) =>
	axios.patch(
		`/luminate/services/${urlParams.serviceId}/offering/${urlParams.offeringId}/update-status`,
		payload,
		{
			params
		}
	)

export interface HttpUpdateIsApprovedGalleryImg {
	urlParams: { serviceId: number; galleryId: number; imgId: number }
	payload: { is_approved: boolean }
	params: { response_type: ResponseType }
}

export const httpUpdateIsApprovedGalleryImg = ({
	urlParams,
	payload,
	params
}: HttpUpdateIsApprovedGalleryImg) =>
	axios.patch(
		`/luminate/services/${urlParams.serviceId}/gallery/${urlParams.galleryId}/images/${urlParams.imgId}/is-approved`,
		payload,
		{ params }
	)

export interface HttpGetServiceFlags {
	params: {
		types?: ServiceFlag['type'][] | null
		response_type: ResponseType
	}
}

export const httpGetServiceFlags = ({ params }: HttpGetServiceFlags) =>
	axios.get('/luminate/services/flags', { params })

export interface HttpEditItemFlags {
	urlParams: {
		serviceId: number
		sectionId?: number
		itemId: number
		section: EditFlagsSectionUrlParam
		item: EditFlagsItemUrlParam
	}
	payload: { flags: number[] }
	params: { response_type: ResponseType }
}

export const httpAddServiceItemFlags = ({
	params,
	payload,
	urlParams
}: HttpEditItemFlags) =>
	axios.post(
		`luminate/services/${urlParams.serviceId}/${urlParams.section}/${urlParams.sectionId}/${urlParams.item}/${urlParams.itemId}/flags`,
		payload,
		{ params }
	)

export const httpRemoveServiceItemFlags = ({
	params,
	payload,
	urlParams
}: HttpEditItemFlags) =>
	axios.delete(
		`luminate/services/${urlParams.serviceId}/${urlParams.section}/${urlParams.sectionId}/${urlParams.item}/${urlParams.itemId}/flags`,
		{
			params: {
				...params,
				...payload
			}
		}
	)

export interface HttpEditServiceSectionFlags {
	urlParams: {
		serviceId: number
		itemId: number
		section: EditFlagsSectionUrlParam
	}
	payload: { flags: number[] }
	params: { response_type: ResponseType }
}

export const httpAddServiceSectionFlags = ({
	params,
	payload,
	urlParams
}: HttpEditServiceSectionFlags) =>
	axios.post(
		`luminate/services/${urlParams.serviceId}/${urlParams.section}/${urlParams.itemId}/flags`,
		payload,
		{
			params
		}
	)

export const httpRemoveServiceSectionFlags = ({
	params,
	payload,
	urlParams
}: HttpEditServiceSectionFlags) =>
	axios.delete(
		`luminate/services/${urlParams.serviceId}/${urlParams.section}/${urlParams.itemId}/flags`,
		{
			params: {
				...params,
				...payload
			}
		}
	)

export interface HttpSubmitService {
	urlParams: {
		serviceId: number
	}
	payload: { is_waitlisted: boolean; is_host_approval_needed: boolean }
	params: { response_type: ResponseType }
}

export const httpSubmitService = ({
	params,
	payload,
	urlParams
}: HttpSubmitService) =>
	axios.post(`luminate/services/${urlParams.serviceId}/submit`, payload, {
		params
	})

//save service merch
export const httpSaveServiceMerch = ({
	params,
	payload,
	urlParams
}: HttpSubmitService) =>
	axios.patch(
		`luminate/services/${urlParams.serviceId}/merchandising-update`,
		payload,
		{ params }
	)

export interface HttpGetAdminServiceVersions {
	urlParams: { serviceId: number }
	params: { response_type: ResponseType }
}

export const httpGetAdminServiceVersions = ({
	urlParams,
	params
}: HttpGetAdminServiceVersions) =>
	axios.get(`luminate/services/${urlParams.serviceId}/versions`, {
		params
	})

interface HttpUploadFileOptions {
	url: string
	payload: File | Blob
	onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
}

export const httpUploadFile = ({
	url,
	payload,
	onUploadProgress
}: HttpUploadFileOptions) =>
	axiosOriginalInstance.put(url, payload, {
		onUploadProgress,
		headers: { 'Content-Type': 'multipart/form-data' }
	})

export interface HttpGetPresignBulkPhotosOptions {
	urlParams: { serviceId: number; galleryId: number }
	payload: { medias: string[]; is_offering_image: boolean }
}
export const httpGetPresignBulkPhotos = ({
	payload,
	urlParams
}: HttpGetPresignBulkPhotosOptions) =>
	axios.post(
		`luminate/services/${urlParams.serviceId}/gallery/${urlParams.galleryId}/images/bulk`,
		payload
	)

export interface HttpGetPresignBulkPhotosProPhotosOptions {
	urlParams: { serviceId: number; galleryId: number }
	payload: { medias: string[]; isOfferingImage: boolean }
}
export const httpGetPresignBulkPhotosProPhotos = ({
	payload,
	urlParams
}: HttpGetPresignBulkPhotosProPhotosOptions) =>
	axiosNode.post(
		`v1/services/vetting/submissions/${urlParams.serviceId}/galleries/${urlParams.galleryId}/images/bulk/upload`,
		payload
	)

/*Confirm upload photo*/
interface HttpConfirmUploadPhotosOptions {
	mediaId: number
	serviceId: number
	galleryId: number
}
export const httpConfirmUploadPhoto = async (
	urlParams: HttpConfirmUploadPhotosOptions
) => {
	const response = await axios.patch(
		`luminate/services/${urlParams.serviceId}/gallery/${urlParams.galleryId}/images/${urlParams.mediaId}/upload-completed`
	)
	return parseResponseData({
		responseData: response.data,
		responseShape: ServiceGalleryImage,
		onZodError: captureException
	})
}

interface HttpConfirmUploadPhotosProPhotosOptions {
	urlParams: { mediaId: number; serviceId: number; galleryId: number }
	params: { responseType: ResponseType }
}
export const httpConfirmUploadPhotoProPhotos = async ({
	urlParams,
	params
}: HttpConfirmUploadPhotosProPhotosOptions) => {
	const response = await axiosNode.patch(
		`v1/services/vetting/submissions/${urlParams.serviceId}/galleries/${urlParams.galleryId}/images/${urlParams.mediaId}/confirm`,
		{ params }
	)
	return parseResponseData({
		responseData: response.data,
		responseShape: ServiceGalleryImageProPhotos,
		onZodError: captureException
	})
}

/*Delete photo*/
export interface HttpDeleteServicePhotoOptions {
	mediaId: number
	serviceId: number
	galleryId: number
}
export const httpDeleteServicePhoto = (
	urlParams: HttpDeleteServicePhotoOptions
) =>
	axios.delete(
		`luminate/services/${urlParams.serviceId}/gallery/${urlParams.galleryId}/images/${urlParams.mediaId}`
	)

export interface HttpDeleteProPhotosServiceOptions {
	serviceId: number
	galleryId: number
	imageIds: number[]
}
export const httpDeleteProPhotosService = (
	urlParams: HttpDeleteProPhotosServiceOptions
) =>
	axiosNode.delete(
		`v1/services/vetting/submissions/${urlParams.serviceId}/galleries/${urlParams.galleryId}/images/bulk/delete`,
		{
			params: {
				imageIds: urlParams.imageIds
			}
		}
	)

export interface HttpUpdateIsCoverGalleryImg {
	urlParams: { serviceId: number; galleryId: number; imgId: number }
	payload: { is_cover: boolean }
	params: { response_type: ResponseType }
}

export const httpUpdateIsCoverGalleryImg = ({
	urlParams,
	payload,
	params
}: HttpUpdateIsCoverGalleryImg) =>
	axios.patch(
		`/luminate/services/${urlParams.serviceId}/gallery/${urlParams.galleryId}/images/${urlParams.imgId}/is-cover`,
		payload,
		{ params }
	)

export interface HttpUpdateIsCoverGalleryImgProPhotos {
	urlParams: { serviceId: number; galleryId: number; imgId: number }
	payload: { isCover: boolean }
	params: { responseType: ResponseType }
}

export const httpUpdateIsCoverGalleryImgProPhotos = async ({
	urlParams,
	payload,
	params
}: HttpUpdateIsCoverGalleryImgProPhotos) => {
	const response = await axiosNode.patch(
		`v1/services/vetting/submissions/${urlParams.serviceId}/galleries/${urlParams.galleryId}/images/${urlParams.imgId}/cover`,
		payload,
		{ params }
	)
	return parseResponseData({
		responseData: response.data,
		responseShape: ServiceProPhotos,
		onZodError: captureException
	})
}

export interface HttpUpdateOfferingMainPhoto {
	urlParams: { serviceId: number; offeringId: number; imgId: number }
	params: { response_type: ResponseType }
}

export const httpUpdateOfferingMainPhoto = ({
	urlParams,
	params
}: HttpUpdateOfferingMainPhoto) =>
	axios.patch(
		`/luminate/services/${urlParams.serviceId}/offering/${urlParams.offeringId}/images/${urlParams.imgId}/change-main-photo`,
		{ params }
	)

export interface HttpUpdateOfferingMainPhotoProPhotos {
	urlParams: { serviceId: number; offeringId: number }
	params: { responseType: ResponseType }
	payload: { coverImageId: number }
}

export const httpUpdateOfferingMainPhotoProPhotos = ({
	urlParams,
	params,
	payload
}: HttpUpdateOfferingMainPhotoProPhotos) =>
	axiosNode.patch(
		`v1/services/vetting/submissions/${urlParams.serviceId}/offerings/${urlParams.offeringId}/cover-image`,
		payload,
		{ params }
	)

export interface HttpRestoreOfferingMainPhoto {
	urlParams: { serviceId: number; offeringId: number }
	params: { response_type: ResponseType }
}

export const httpRestoreOfferingMainPhoto = ({
	urlParams,
	params
}: HttpRestoreOfferingMainPhoto) =>
	axios.patch(
		`/luminate/services/${urlParams.serviceId}/offering/${urlParams.offeringId}/images/restore-main-photo`,
		{ params }
	)

export interface HttpGetServiceGalleryImagesOptions {
	urlParams: { serviceId: number; galleryId: number }
	params: { response_type: ResponseType }
}

export const HttpGetServiceGalleryImagesResponse = z.array(ServiceGalleryImage)
export type HttpGetServiceGalleryImagesResponse = z.infer<
	typeof HttpGetServiceGalleryImagesResponse
>

export const httpGetServiceGalleryImages = ({
	urlParams,
	params
}: HttpGetServiceGalleryImagesOptions) =>
	axios.get(
		`/luminate/services/${urlParams.serviceId}/gallery/${urlParams.galleryId}/images`,
		{ params }
	)

export interface HttpUpdateServices {
	urlParams: { serviceId: number }
	payload: { is_pro_photography_required: boolean }
	params: { response_type: ResponseType }
}

export const httpUpdateServices = ({
	urlParams,
	payload,
	params
}: HttpUpdateServices) =>
	axios.patch(`/luminate/services/${urlParams.serviceId}`, payload, {
		params
	})

export interface HttpUpdateServiceVettingGalleryImagesPositionsOptions {
	urlParams: { serviceId: number; galleryId: number }
	payload: { id: number; position: number }[]
	params: { responseType: ResponseType }
}

export const httpUpdateServiceVettingGalleryImagesPositions = ({
	urlParams,
	payload,
	params
}: HttpUpdateServiceVettingGalleryImagesPositionsOptions) =>
	axiosNode.patch(
		`v1/services/vetting/submissions/${urlParams.serviceId}/galleries/${urlParams.galleryId}/images/update-positions`,
		payload,
		{ params }
	)

export interface HttpUpdateProPhotosListing {
	urlParams: {
		serviceId: number
	}
	payload: { isWaitlisted: boolean; isHostApprovalNeeded: boolean }
	params: { responseType: ResponseType }
}

export const httpUpdateProPhotosListing = ({
	params,
	payload,
	urlParams
}: HttpUpdateProPhotosListing) =>
	axiosNode.post(
		`v1/services/vetting/submissions/${urlParams.serviceId}/update-listing`,
		payload,
		{ params }
	)

export interface HttpGetServiceGalleryImagesProPhotosOptions {
	urlParams: { serviceId: number; galleryId: number }
	params: { responseType: ResponseType }
}

export const HttpGetServiceGalleryImagesProPhotosResponse = z.array(
	ServiceGalleryImageProPhotos
)
export type HttpGetServiceGalleryImagesProPhotosResponse = z.infer<
	typeof HttpGetServiceGalleryImagesProPhotosResponse
>

export const httpGetServiceGalleryImagesProPhotos = async ({
	urlParams,
	params
}: HttpGetServiceGalleryImagesProPhotosOptions) => {
	const response = await axiosNode.get(
		`v1/services/vetting/submissions/${urlParams.serviceId}/galleries/${urlParams.galleryId}/images`,
		{ params }
	)

	return parseResponseData({
		responseData: response.data,
		responseShape: HttpGetServiceGalleryImagesProPhotosResponse,
		onZodError: captureException
	})
}
